<i18n>
{
	"en": {
		"name": "Name",
		"nameNotSet": "Name is a mandatory field",
		"nameInUse": "The name is already in use",
		"fromAddress": "From Address",
		"toAddress": "To Address",
		"duration": "Duration",
		"days": "Day | Days",
		"departure": "Departure time",
		"arrival": "Arrival time",
		"selectTime": "Select time",
		"invalidDateObject": "Time is a mandatory field",
		"timeNotSet": "Time is a mandatory field",
		"date1IsNotBeforeDate2": "Planned departure is before planned arrival."
	},
	"no": {
		"name": "Navn",
		"nameNotSet": "Navn er påkrevd",
		"nameInUse": "Navnet er allerede i bruk",
		"fromAddress": "Fra adresse",
		"toAddress": "Til adresse",
		"duration": "Varighet",
		"days": "Dag | Dager",
		"departure": "Avgangstid",
		"arrival": "Ankomsttid",
		"selectTime": "Velg tid",
		"invalidDateObject": "Tidspunkt er påkrevd",
		"timeNotSet": "Tidspunkt er påkrevd",
		"date1IsNotBeforeDate2": "Planlagt avgang er før planlagt ankomst."
	}
}
</i18n>

<template>
	<div class="new-template">
		<div class="grid-top">

			<t-form-group :title="$t('name')" rowGap="5px">
				<t-text-input
					:value="templateDetails.name"
					:isInvalid="Boolean(errors.name)"
					:validationMessage="$t(errors.name)"
					@input="setName"
				/>
			</t-form-group>

			<OperationalStatusFormInput
				:availableStatuses="availableStatuses"
				:selectedItem="templateDetails.status"
				@change="setStatus"
			/>
			<TripTypesFormInput
				:selectedItem="templateDetails.type"
				@select="setType"
			/>

			<ClerkInput
				:value="templateDetails.clerk"
				@change="setClerk"
			/>

		</div>
		<hr>
		<div class="grid-left">

			<TripAddressInput
				:title="$t('fromAddress')"
				:value="templateDetails.fromAddress"
				:isInvalid="Boolean(errors.fromAddress)"
				:validationKey="errors.fromAddress"
				focusNextOnSelected
				@change="setFromAddress"
			/>

			<t-form-group :title="$t('departure')" rowGap="5px">
				<t-time-picker
					:value="departureTime"
					:placeholder="$t('selectTime')"
					:isInvalid="Boolean(errors.etd)"
					:validationMessage="$t(errors.etd)"
					@input="departureTime = $event"
				/>
			</t-form-group>

			<t-form-group :title="$t('duration')" rowGap="5px">
				<t-number-input
					:value="templateDetails.duration"
					:extraDisplayText="$tc('days', templateDetails.duration)"
					castToNumber
					leftAlign
					@focus="$event.target.select()"
					@change="setDuration"
				/>
			</t-form-group>

			<TripAddressInput
				:title="$t('toAddress')"
				:value="templateDetails.toAddress"
				:isInvalid="Boolean(errors.toAddress)"
				:validationKey="errors.toAddress"
				focusNextOnSelected
				@change="setToAddress"
			/>

			<t-form-group :title="$t('arrival')" rowGap="5px">
				<t-time-picker
					:value="arrivalTime"
					:placeholder="$t('selectTime')"
					:isInvalid="Boolean(errors.eta)"
					:validationMessage="$t(errors.eta)"
					@input="arrivalTime = $event"
				/>
			</t-form-group>

		</div>
		<div class="grid-right">

			<CargoCarriersInput
				:value="templateDetails.cargoCarrier"
				@change="setCargoCarrier"
			/>
			<VendorInput
				ref="VendorsFormInput"
				:value="templateDetails.vendor"
				@change="setVendor"
			/>

		</div>

		<Legs v-if="!loading" class="grid-bottom"/>
		<t-overlay
			:isShown="loading"
			loaderMarginBottom="50px"
			absolute
			loader
		/>

	</div>
</template>

<script>
import { mapState, mapActions } from '../store/tripTemplates.store';
import { statuses, } from '@/core';
import moment from 'moment';
import OperationalStatusFormInput from '@/modules/operationalStatus/OperationalStatusFormInput.vue';
import TripTypesFormInput from '@/modules/tripTypes/TripTypesFormInput.vue';
import ClerkInput from '@/modules/clerks/ClerkInput.vue';
import TripAddressInput from '@/modules/tripAddresses/TripAddressInput';
import CargoCarriersInput from '@/modules/cargoCarriers/CargoCarriersInput.vue';
import VendorInput from '@/modules/vendors/VendorInput.vue';

import Legs from './Legs/Legs';

export default {
	name: 'TripTemplateData',
	components: {
		OperationalStatusFormInput,
		TripTypesFormInput,
		ClerkInput,
		TripAddressInput,
		CargoCarriersInput,
		VendorInput,
		Legs,
	},

	data() {
		return {
			availableStatuses: Object.values(statuses.trip),
			frequencies: [ 'daily', 'weekly', 'twoWeekly', 'monthly', 'specificDates' ],
			days: [ 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday' ],
			numbers: [ 1, 2, 3, 4 ],
		};
	},

	computed: {
		...mapState(['templateDetails', 'errors', 'loading']),
		departureTime: {
			get() {
				return this.templateDetails.departureTime ? moment(this.templateDetails.departureTime, 'HH:mm').format('LT') : '';
			},
			set(value) {
				this.setDepartureTime(value);
			}
		},
		arrivalTime: {
			get() {
				return this.templateDetails.arrivalTime ? moment(this.templateDetails.arrivalTime, 'HH:mm').format('LT') : '';
			},
			set(value) {
				this.setArrivalTime(value);
			}
		},
	},

	methods: {
		...mapActions([
			'setName',
			'setDepartureTime',
			'setArrivalTime',
			'setDuration',
			'setStatus',
			'setType',
			'setClerk',
			'setFromAddress',
			'setToAddress',
			'setCargoCarrier',
			'setVendor',
		]),
	}
};
</script>

<style lang="scss" scoped>
@import '~@/components/evergreen/scss/colors';

.new-template {
	display: grid;
	row-gap: 20px;
	grid-template-columns: 708px 1fr;
	grid-template-areas:
		"top	top"
		".....	....."
		"left	right"
		"bottom bottom";
	margin-top: 20px;
	transition: 0.3s;
}

hr {
	grid-column: 1 / -1;
	height: 1px;
	background: get-color($border, 'default');
	width: 100%;
	margin-top: 0;
	margin-bottom: 0;
	border: 0;
}

.grid-top {
	display: grid;
	row-gap: 20px;
	grid-template-columns: 	repeat(4, 1fr);
	grid-area: top;

	& > * {
			padding-right: 20px;
			width: auto;
	}
	& > *:last-child {
			padding-right: 0px;
	}
}
.grid-left {
	display: grid;
	row-gap: 20px;
	column-gap: 20px;
	grid-area: left;
	grid-template-columns: 	318px
							110px
							1fr;
	grid-template-rows: auto 1fr;
}

.grid-right {
	display: grid;
	row-gap: 20px;
	grid-area: right;
	grid-template-columns: 1fr;
	grid-template-rows: repeat(3, minmax(min-content, max-content));

	border-left: 1px solid get-color($border, 'default');
	padding-left: 19px;
	margin-left: 20px;
}

.grid-bottom {
	grid-area: bottom
}
</style>
